import React, { useState, useEffect } from "react";
import { Button as BSButton } from "react-bootstrap";
import {
  Table,
  Button,
  Tag,
  Typography,
  Space,
  Card,
  message,
  DatePicker,
  Input,
  Drawer,
  Form,
  Divider,
  Dropdown,
  Timeline,
  Modal,
} from "antd";
import dayjs from "dayjs";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  MinusCircleTwoTone,
  EditOutlined,
  EllipsisOutlined,
  HistoryOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

const { TextArea } = Input;

const seedChecklistData = [
  {
    key: "1",
    feature: "Users",
    subFeatures: [
      "Customers",
      "Staff",
      "User Affiliations",
      "User Deletion Request",
      "Guest Sessions",
      "Feedbacks",
      "Help and Support",
    ],
  },
  {
    key: "2",
    feature: "Tasks",
    subFeatures: ["Categories", "All Tasks", "Task Management"],
  },
  {
    key: "3",
    feature: "Donations",
    subFeatures: [
      "Donations",
      "Donation Purpose",
      "Credit Applications",
      "Issued Credits",
    ],
  },
  {
    key: "4",
    feature: "Payments",
    subFeatures: [
      "Payment Methods",
      "Transaction History",
      "Refunds",
      "Payment Settings",
    ],
  },
  {
    key: "5",
    feature: "Notifications",
    subFeatures: [
      "Push Notifications",
      "Email Notifications",
      "In-App Messages",
      "Notification Preferences",
    ],
  },
  {
    key: "6",
    feature: "Reports",
    subFeatures: ["Dashboard", "Analytics", "Export Data", "Scheduled Reports"],
  },
  {
    key: "7",
    feature: "Settings",
    subFeatures: [
      "Profile Settings",
      "App Preferences",
      "Security Settings",
      "API Keys",
    ],
  },
  {
    key: "8",
    feature: "Integrations",
    subFeatures: [
      "Third-Party Apps",
      "Webhooks",
      "OAuth",
      "API Integrations",
    ],
  },
  {
    key: "9",
    feature: "Content",
    subFeatures: ["Pages", "Media Library", "SEO Settings", "Templates"],
  },
  {
    key: "10",
    feature: "Audit Logs",
    subFeatures: ["Activity Logs", "User Actions", "System Events", "Export Logs"],
  },
];

const statusIcons = {
  PASS: <CheckCircleTwoTone twoToneColor="#52c41a" />,
  FAIL: <CloseCircleTwoTone twoToneColor="#ff4d4f" />,
  NONE: <MinusCircleTwoTone twoToneColor="#bfbfbf" />,
};

const statusTags = {
  PASS: <Tag color="success">PASS</Tag>,
  FAIL: <Tag color="error">FAIL</Tag>,
  NONE: <Tag color="default">Not Set</Tag>,
};

const STORAGE_KEY = "qa_checklist_results";
const HISTORY_KEY = "qa_checklist_history";
const FEATURES_KEY = "qa_checklist_features";

export default function QAChecklistSection() {
  const [checklistData, setChecklistData] = useState(() => {
    try {
      const stored = localStorage.getItem(FEATURES_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return seedChecklistData;
  });

  const [results, setResults] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return {};
  });

  const [history, setHistory] = useState(() => {
    try {
      const stored = localStorage.getItem(HISTORY_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return [];
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedFeatureKey, setSelectedFeatureKey] = useState(null);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [historyTitle, setHistoryTitle] = useState("");
  const [historyItems, setHistoryItems] = useState([]);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editContext, setEditContext] = useState(null); // { mode: 'add'|'edit', type:'feature'|'subFeature', featureKey, originalName? }

  useEffect(() => {
    try {
      localStorage.setItem(FEATURES_KEY, JSON.stringify(checklistData));
    } catch {}
  }, [checklistData]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(results));
    } catch {}
  }, [results]);

  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
    } catch {}
  }, [history]);

  const openDrawer = (featureKey, subFeature) => {
    setSelectedFeatureKey(featureKey);
    const existing = results[featureKey]?.[subFeature];
    setEditingRecord({ featureKey, subFeature, ...existing });
    form.setFieldsValue({
      result: existing?.result || null,
      testDate: existing?.testDate ? dayjs(existing.testDate) : dayjs(),
      notes: existing?.notes || "",
    });
    setDrawerOpen(true);
  };

  const openEditModal = (ctx) => {
    setEditContext(ctx);
    if (ctx.type === "feature") {
      const current = checklistData.find((f) => f.key === ctx.featureKey);
      editForm.setFieldsValue({
        feature: ctx.mode === "add" ? "" : current?.feature || "",
      });
    } else {
      editForm.setFieldsValue({
        subFeature: ctx.mode === "add" ? "" : ctx.originalName || "",
      });
    }
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditContext(null);
    editForm.resetFields();
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const saveResult = (values) => {
    const { result, testDate, notes } = values;
    if (!result) {
      message.warning("Please select Pass or Fail");
      return;
    }

    const testDateISO = testDate?.toISOString() || new Date().toISOString();
    const notesTrimmed = notes?.trim() || "";

    setResults((prev) => ({
      ...prev,
      [selectedFeatureKey]: {
        ...(prev[selectedFeatureKey] || {}),
        [editingRecord.subFeature]: {
          result,
          testDate: testDateISO,
          notes: notesTrimmed,
        },
      },
    }));

    setHistory((prev) => [
      {
        id: `history-${Date.now()}`,
        featureKey: selectedFeatureKey,
        subFeature: editingRecord.subFeature,
        result,
        testDate: testDateISO,
        notes: notesTrimmed,
        timestamp: new Date().toISOString(),
      },
      ...prev,
    ]);

    message.success(`${result} recorded for ${editingRecord.subFeature}`);
    closeDrawer();
  };

  const renameSubFeatureInResultsAndHistory = (featureKey, oldName, newName) => {
    if (!oldName || !newName || oldName === newName) return;
    setResults((prev) => {
      const featureResults = prev?.[featureKey];
      if (!featureResults || typeof featureResults !== "object") return prev;
      if (!Object.prototype.hasOwnProperty.call(featureResults, oldName)) return prev;
      if (Object.prototype.hasOwnProperty.call(featureResults, newName)) return prev;
      const nextFeatureResults = { ...featureResults, [newName]: featureResults[oldName] };
      delete nextFeatureResults[oldName];
      return { ...prev, [featureKey]: nextFeatureResults };
    });

    setHistory((prev) =>
      prev.map((h) => {
        if (h.featureKey === featureKey && h.subFeature === oldName) return { ...h, subFeature: newName };
        return h;
      }),
    );
  };

  const deleteFeatureData = (featureKey) => {
    setChecklistData((prev) => prev.filter((f) => f.key !== featureKey));
    setResults((prev) => {
      const next = { ...(prev || {}) };
      delete next[featureKey];
      return next;
    });
    setHistory((prev) => prev.filter((h) => h.featureKey !== featureKey));
  };

  const deleteSubFeatureData = (featureKey, subFeature) => {
    setChecklistData((prev) =>
      prev.map((f) => (f.key === featureKey ? { ...f, subFeatures: f.subFeatures.filter((sf) => sf !== subFeature) } : f)),
    );
    setResults((prev) => {
      const featureResults = prev?.[featureKey];
      if (!featureResults || typeof featureResults !== "object") return prev;
      const nextFeatureResults = { ...featureResults };
      delete nextFeatureResults[subFeature];
      return { ...prev, [featureKey]: nextFeatureResults };
    });
    setHistory((prev) => prev.filter((h) => !(h.featureKey === featureKey && h.subFeature === subFeature)));
  };

  const saveEdit = async () => {
    const ctx = editContext;
    if (!ctx) return;
    const values = await editForm.validateFields();

    if (ctx.type === "feature") {
      const name = String(values.feature || "").trim();
      if (!name) {
        message.warning("Feature name is required");
        return;
      }
      if (ctx.mode === "add") {
        const key = `f-${Date.now()}`;
        setChecklistData((prev) => [{ key, feature: name, subFeatures: [] }, ...(prev || [])]);
        message.success("Feature added");
      } else {
        setChecklistData((prev) => prev.map((f) => (f.key === ctx.featureKey ? { ...f, feature: name } : f)));
        message.success("Feature updated");
      }
      closeEditModal();
      return;
    }

    const subName = String(values.subFeature || "").trim();
    if (!subName) {
      message.warning("Sub-feature name is required");
      return;
    }

    const target = checklistData.find((f) => f.key === ctx.featureKey);
    const existing = new Set((target?.subFeatures || []).map((s) => String(s).toLowerCase()));
    const desiredKey = String(subName).toLowerCase();
    if (ctx.mode === "add" && existing.has(desiredKey)) {
      message.warning("This sub-feature already exists");
      return;
    }
    if (ctx.mode === "edit" && ctx.originalName && String(ctx.originalName).toLowerCase() !== desiredKey && existing.has(desiredKey)) {
      message.warning("This sub-feature already exists");
      return;
    }

    if (ctx.mode === "add") {
      setChecklistData((prev) =>
        prev.map((f) => (f.key === ctx.featureKey ? { ...f, subFeatures: [...f.subFeatures, subName] } : f)),
      );
      message.success("Sub-feature added");
    } else {
      setChecklistData((prev) =>
        prev.map((f) => {
          if (f.key !== ctx.featureKey) return f;
          const next = f.subFeatures.map((sf) => (sf === ctx.originalName ? subName : sf));
          return { ...f, subFeatures: next };
        }),
      );
      renameSubFeatureInResultsAndHistory(ctx.featureKey, ctx.originalName, subName);
      message.success("Sub-feature updated");
    }
    closeEditModal();
  };

  const viewHistory = (type, featureKey, subFeature = null) => {
    const featureName = checklistData.find((f) => f.key === featureKey)?.feature || "";

    if (type === "feature") {
      const featureHistory = history.filter((h) => h.featureKey === featureKey);
      setHistoryTitle(`History: ${featureName}`);
      setHistoryItems(featureHistory);
    } else {
      const subFeatureHistory = history.filter(
        (h) => h.featureKey === featureKey && h.subFeature === subFeature
      );
      setHistoryTitle(`History: ${featureName} > ${subFeature}`);
      setHistoryItems(subFeatureHistory);
    }
    setHistoryDrawerOpen(true);
  };

  const closeHistoryDrawer = () => {
    setHistoryDrawerOpen(false);
    setHistoryItems([]);
    setHistoryTitle("");
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    return dayjs(isoString).format("MMMM D, YYYY hh:mm A");
  };

  const getFeatureActions = (record) => ({
    items: [
      {
        key: "addSubFeature",
        label: "Add Sub-Feature",
        icon: <PlusOutlined />,
        onClick: () => openEditModal({ type: "subFeature", mode: "add", featureKey: record.key }),
      },
      {
        key: "viewHistory",
        label: "View Feature History",
        icon: <HistoryOutlined />,
        onClick: () => viewHistory("feature", record.key),
      },
      {
        key: "editFeature",
        label: "Edit Feature",
        icon: <EditOutlined />,
        onClick: () => openEditModal({ type: "feature", mode: "edit", featureKey: record.key }),
      },
      {
        type: "divider",
      },
      {
        key: "deleteFeature",
        label: "Delete Feature",
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => {
          Modal.confirm({
            title: "Delete feature?",
            content: "This will delete the feature, its sub-features, results, and history.",
            okText: "Delete",
            okButtonProps: { danger: true },
            onOk: () => deleteFeatureData(record.key),
          });
        },
      },
    ],
  });

  const getSubFeatureActions = (record, row) => ({
    items: [
      {
        key: "viewHistory",
        label: "View History",
        icon: <HistoryOutlined />,
        onClick: () => viewHistory("subFeature", record.key, row.subFeature),
      },
      {
        key: "editSubFeature",
        label: "Edit",
        icon: <EditOutlined />,
        onClick: () =>
          openEditModal({
            type: "subFeature",
            mode: "edit",
            featureKey: record.key,
            originalName: row.subFeature,
          }),
      },
      {
        type: "divider",
      },
      {
        key: "deleteSubFeature",
        label: "Delete",
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => {
          Modal.confirm({
            title: "Delete sub-feature?",
            content: `Delete "${row.subFeature}" and its recorded results/history?`,
            okText: "Delete",
            okButtonProps: { danger: true },
            onOk: () => deleteSubFeatureData(record.key, row.subFeature),
          });
        },
      },
    ],
  });

  const columns = [
    {
      title: "Feature",
      dataIndex: "feature",
      key: "feature",
      render: (text) => <Typography.Text strong>{text}</Typography.Text>,
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        const featureResults = results[record.key] || {};
        const stats = { PASS: 0, FAIL: 0, NONE: 0 };
        Object.values(featureResults).forEach((val) => {
          if (val?.result === "PASS") stats.PASS++;
          else if (val?.result === "FAIL") stats.FAIL++;
          else stats.NONE++;
        });
        const total = record.subFeatures.length;
        const tested = stats.PASS + stats.FAIL;
        let color = "default";
        if (stats.FAIL > 0) color = "error";
        else if (tested === total && stats.PASS === total) color = "success";
        else if (tested > 0) color = "processing";
        return (
          <Tag color={color}>
            {stats.PASS} Pass / {stats.FAIL} Fail / {stats.NONE} Untested
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "actions",
      width: 110,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Dropdown trigger={["click"]} menu={getFeatureActions(record)}>
          <Button size="small" icon={<EllipsisOutlined />}>
            Actions
          </Button>
        </Dropdown>
      ),
    },
  ];

  const expandedRowRender = (record) => (
    <Table
      columns={[
        {
          title: "Sub-Feature",
          dataIndex: "subFeature",
          key: "subFeature",
          width: 180,
        },
        {
          title: "Test Date",
          key: "testDate",
          width: 150,
          render: (_, row) => {
            const data = results[record.key]?.[row.subFeature];
            return data?.testDate ? formatDateTime(data.testDate) : "-";
          },
        },
        {
          title: "Notes",
          key: "notes",
          width: 200,
          ellipsis: true,
          render: (_, row) => {
            const data = results[record.key]?.[row.subFeature];
            return data?.notes ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {data.notes.length > 50 ? data.notes.slice(0, 50) + "..." : data.notes}
              </Typography.Text>
            ) : (
              "-"
            );
          },
        },
        {
          title: "Status",
          key: "status",
          width: 100,
          render: (_, row) => {
            const data = results[record.key]?.[row.subFeature];
            return (
              <Space>
                {statusIcons[data?.result || "NONE"]}
                {statusTags[data?.result || "NONE"]}
              </Space>
            );
          },
        },
        {
          title: "Action",
          key: "actions",
          width: 160,
          align: "center",
          fixed: "right",
          render: (_, row) => (
            <Space>
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => openDrawer(record.key, row.subFeature)}
              >
                {results[record.key]?.[row.subFeature]?.result ? "Edit" : "Test"}
              </Button>
              <Dropdown trigger={["click"]} menu={getSubFeatureActions(record, row)}>
                <Button size="small" icon={<EllipsisOutlined />}>
                  More
                </Button>
              </Dropdown>
            </Space>
          ),
        },
      ]}
      dataSource={record.subFeatures.map((sf) => ({ key: sf, subFeature: sf }))}
      pagination={false}
      rowKey="subFeature"
      size="small"
      bordered
      tableLayout="fixed"
      scroll={{ x: 920 }}
    />
  );

  return (
    <>
      <Card>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openEditModal({ type: "feature", mode: "add" })}
          >
            New Feature
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={checklistData}
          expandable={{ expandedRowRender, defaultExpandAllRows: true }}
          pagination={{
            defaultPageSize: 5,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10"],
            hideOnSinglePage: true,
            size: "small",
          }}
          rowKey="key"
          bordered
          size="small"
          tableLayout="fixed"
          scroll={{ x: 920 }}
          style={{ borderRadius: 8, overflow: "hidden" }}
        />
      </Card>

      <Modal
        title={
          editContext?.type === "feature"
            ? editContext?.mode === "add"
              ? "Add Feature"
              : "Edit Feature"
            : editContext?.mode === "add"
              ? "Add Sub-Feature"
              : "Edit Sub-Feature"
        }
        open={editModalOpen}
        onCancel={closeEditModal}
        onOk={saveEdit}
        okText={editContext?.mode === "add" ? "Add" : "Save"}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          {editContext?.type === "feature" ? (
            <Form.Item
              name="feature"
              label="Feature name"
              rules={[{ required: true, message: "Feature name is required" }]}
            >
              <Input placeholder="e.g. Payments" />
            </Form.Item>
          ) : (
            <Form.Item
              name="subFeature"
              label="Sub-feature name"
              rules={[{ required: true, message: "Sub-feature name is required" }]}
            >
              <Input placeholder="e.g. Refunds" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Drawer
        title={`Test: ${editingRecord?.subFeature}`}
        open={drawerOpen}
        onClose={closeDrawer}
        width={400}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={saveResult}>
          <Form.Item name="result" label="Result" rules={[{ required: true, message: "Select result" }]}>
            <Space>
              <BSButton
                variant={form.getFieldValue("result") === "PASS" ? "success" : "outline-success"}
                onClick={() => form.setFieldsValue({ result: "PASS" })}
              >
                Pass
              </BSButton>
              <BSButton
                variant={form.getFieldValue("result") === "FAIL" ? "danger" : "outline-danger"}
                onClick={() => form.setFieldsValue({ result: "FAIL" })}
              >
                Fail
              </BSButton>
            </Space>
          </Form.Item>

          <Form.Item name="testDate" label="Test Date & Time (auto)">
            <DatePicker
              showTime={{ format: "hh:mm A" }}
              format="MMMM D, YYYY hh:mm A"
              style={{ width: "100%" }}
              placeholder="Select date and time"
              disabled
            />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <TextArea
              rows={4}
              placeholder="Add notes about the test..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Divider />

          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Save Result
            </Button>
          </Space>
        </Form>
      </Drawer>

      <Drawer
        title={historyTitle}
        open={historyDrawerOpen}
        onClose={closeHistoryDrawer}
        width={450}
      >
        {historyItems.length === 0 ? (
          <Typography.Text type="secondary">No test history found.</Typography.Text>
        ) : (
          <Timeline
            items={historyItems.map((item) => ({
              color: item.result === "PASS" ? "green" : "red",
              children: (
                <Space direction="vertical" size={0}>
                  <Space>
                    {statusIcons[item.result]}
                    <Typography.Text strong>{item.result}</Typography.Text>
                    <Typography.Text type="secondary">
                      {formatDateTime(item.testDate)}
                    </Typography.Text>
                  </Space>
                  {item.notes && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {item.notes}
                    </Typography.Text>
                  )}
                </Space>
              ),
            }))}
          />
        )}
      </Drawer>
    </>
  );
}
